import React from 'react';
import { Helmet } from "react-helmet";
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import Checkbox from '../audi-ui-components/Checkbox';
import TextField from '../audi-ui-components/TextField';
import Button from '../audi-ui-components/Button';
import IconSelect from '../audi-ui-components/icons/Select';
import IconCaution from '../audi-ui-components/icons/Caution';
import Spinner from '../audi-ui-components/Spinner';
import Loader from '../components/Loader';
import { Formik, Form } from 'formik';
import ReCAPTCHA from "react-google-recaptcha";
import queryString from 'query-string';
import { request, prefCentreService } from '../functions/apiRequestHelper';
import { gtmPageview, gtmPush } from '../functions/gtm';
import { validate } from '../functions/validation';
import {
    UI_AUDI_MIN,
    PATH_PREFERENCECENTRE,
    PATH_CONTACTUS,
    DEFAULT_THEME,
} from '../constants';

// feature toggle
const USE_EMAIL_FALLBACK = false;

const optInLabels = {
  "pcProductOptIn": (<span><b>Vehicle Information</b> (Occasional)<br />Relevant new and existing Audi models and vehicle innovations.</span>),
  "pcNewsOptIn": (<span><b>Audi news</b> (Quarterly)<br />The latest general news from Audi.</span>),
  "pcOffersOptIn": (<span><b>Offers</b> (Occasional)<br />Notification of special offers on Audi products.</span>),
  "pcF1OptIn": (<span><b>Audi Formula 1 updates</b> (Occasional)<br />Be the first to hear the latest from Audi in F1, including news, ticket releases and exclusive experiences.</span>),
  "pcMagazineOptIn": (<span><b>Audi Magazine</b> (Monthly)<br />Highlights on Audi{`'`}s lifestyle, culture, sport and ambassador activities.</span>),
  "pcExperienceOptIn": (<span><b>Audi Experience</b> (Occasional)<br />Audi loyalty program benefits and invitations.</span>),
  "pcFoundationOptIn": (<span><b>Audi Foundation</b> (Occasional)<br />Audi{`'`}s community and charitable projects.</span>),
  "pcEventsOptIn": (<span><b>Audi Events</b> (Occasional)<br />Invitations to events including the Audi driving experience and product launches.</span>),
  "pcSurveyOptIn": (<span><b>Audi Surveys</b> (Occasional)<br />Tell us how we can enhance your experience with Audi products and programs.</span>),
  "pcFinancialServiceOptIn": (<span><b>Financial services</b> (Occasional)<br />Tailored finance solutions that suit your budget and your new Audi.</span>),
  "pcStoreOptIn": (<span><b>Audi Store</b> (Occasional)<br />Offers, products, and accessories from Audi Store.</span>),
  "pcsmsOptIn": (<span><b>SMS me</b> - Timely reminders for relevant events.</span>)
};

const unsubListNames = {
  "product": "Vehicle Information",
  "news": "Audi News",
  "offers": "Special Offers",
  "magazine": "Audi Magazine",
  "experience": "Audi Experience",
  "foundation": "Audi Foundation",
  "events": "Audi Events",
  "survey": "Audi Surveys",
  "surveys": "Audi Surveys",
  "financialService": "Financial Services",
  "store": "Audi Store",
  "sms": "SMS me"
};

const PreferenceOption = ({ name, className = "", setFieldValue, values, theme }) => {
  return (
    <div className={`form-field ${className}`}>
      <Checkbox name={name}
        label={optInLabels[name]}
        checked={!!values[name]}
        value=""
        onChange={(v,e) => { setFieldValue(name, e.target.checked); }}
        theme={theme === "dark" ? "light" : null}
      />
    </div>
  );
}

class PreferenceCentre extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            user: null,
            isFetchingUser: true,
            unsubscribing: false,
            unsubError: false,
            unsubSuccess: false,
            unsubList: null,
            updateSuccess: false,
            submitError: false,
            userIdError: false,
            submitEmailError: false,
            submitEmailSuccess: false,
            capVal: false
        }
    }

    componentDidMount() {
        const {match} = this.props;
        let query = queryString.parse(this.props.location.search);
        let userSfid = query.sfid || match.params.user;
        if (!userSfid) {
          this.setState({isFetchingUser: false, userIdError: true});
        } else {
          prefCentreService.get(userSfid).then((result) => {
            
            if (query.list) {
              // one-click unsub
              let list = query.list.split(",");
              this.setState({user: result, isFetchingUser: false, unsubscribing: true, unsubList: list});
              request(`${process.env.RAZZLE_API_HOST}/1/account/profile/${result.id}/preferenceCentre`, {
                method: "PATCH",
                body: JSON.stringify({
                  preferenceTypes: list
                })
              }).then(() => {
                this.setState({unsubscribing: false, unsubSuccess: true});
                // fetch updated prefs
                this.setState({isFetchingUser: true});
                prefCentreService.get(userSfid).then((userUpdate) => {
                  this.setState({user: userUpdate, isFetchingUser: false})
                }).catch((error) => {
                  console.error('user error', error);
                  this.setState({isFetchingUser: false});
                });
              }).catch((err) => {
                console.error(err);
                formikProps.setSubmitting(false);
                this.setState({submitEmailError: true});
              });
            } else {
              // normal prefs form
              this.setState({user: result, isFetchingUser: false});
            }
            
          }).catch((error) => {
            console.error('user error', error);
            this.setState({isFetchingUser: false, userIdError: true});
          });
        }
        gtmPageview(PATH_PREFERENCECENTRE);
    }

    handleSubmit = (values, formikProps) => {
        // console.log("submitting preferences...");
        this.setState({updateSuccess: false, submitError: false});
        let u = false;
        if (
            !values.pcProductOptIn &&
            !values.pcNewsOptIn &&
            !values.pcOffersOptIn &&
            !values.pcF1OptIn &&
            !values.pcMagazineOptIn &&
            !values.pcExperienceOptIn &&
            !values.pcFoundationOptIn &&
            !values.pcEventsOptIn &&
            !values.pcSurveyOptIn &&
            !values.pcFinancialServiceOptIn &&
            !values.pcStoreOptIn &&
            !values.pcsmsOptIn
        ) { u = true; }
        values.unsubscribe = u;
        values.url = window.location.href;
        let query = queryString.parse(this.props.location.search);
        values.campaign = query.utm_campaign;
        // console.log(values);
        gtmPush("form", "formSubmit", "preferenceCentre", values);
        prefCentreService.patch(values).then(() => {
            // console.log("preferences updated");
            formikProps.setSubmitting(false);
            this.setState({updateSuccess: true, submitError: false});
            // thneed.trigger(thneed.events.formSubmitSuccess, 'preferenceCentre', {formData: values, formName: 'preferenceCentre'});
            gtmPush("form", "formSubmitSuccess", "preferenceCentre", values);
        }).catch((error) => {
            console.log("update failed", error);
            formikProps.setSubmitting(false);
            this.setState({submitError: true});
            // thneed.trigger(thneed.events.formSubmitError, 'preferenceCentre', {formData: values, formName: 'preferenceCentre'});
            gtmPush("form", "formSubmitError", "preferenceCentre", values);
        });
    }
    
    handleSubmitEmail = (values, formikProps) => {
        const {match} = this.props;
        let query = queryString.parse(this.props.location.search);
        let sfid = (query && query.sfid) ? query.sfid : match.params.user;
        request(`${process.env.RAZZLE_API_HOST}/1/account/unsubscribeAll`, {
            method: "POST",
            body: JSON.stringify({
                email: values.emailAddress,
                captchaToken: this.state.capVal,
                subscriberKey: sfid
            })
        }).then((res) => {
            console.log(res);
            formikProps.setSubmitting(false);
            this.setState({submitEmailSuccess: true, submitEmailError: false});
        }).catch((err) => {
            console.error(err);
            formikProps.setSubmitting(false);
            this.setState({submitEmailError: true});
        });
    }
    
    validateEmailForm = (values) => {
        var schema = {
            emailAddress: {
                presence: {
                    message: "Email address is required",
                    allowEmpty: false
                },
                email: {message: "Please enter a valid email address"}
            }
        };
        var v = validate(values, schema, {format: "firstError", fullMessages: false});
        return v;
    }
    
    handleToggleAll = (values, setValues) => {
      let newValue = true;
      if (values.pcProductOptIn && values.pcNewsOptIn && values.pcOffersOptIn && values.pcF1OptIn && values.pcMagazineOptIn && values.pcExperienceOptIn && values.pcFoundationOptIn && values.pcEventsOptIn && values.pcSurveyOptIn && values.pcStoreOptIn) {
        // all are currently checked
        newValue = false;
      }
      setValues(Object.assign({}, values, {
        pcProductOptIn: newValue,
        pcNewsOptIn: newValue,
        pcOffersOptIn: newValue,
        pcF1OptIn: newValue,
        pcMagazineOptIn: newValue,
        pcExperienceOptIn: newValue,
        pcFoundationOptIn: newValue,
        pcEventsOptIn: newValue,
        pcSurveyOptIn: newValue,
        pcFinancialServiceOptIn: newValue,
        pcsmsOptIn: newValue,
        pcStoreOptIn: newValue
      }));
    }

    render() {
        const query = queryString.parse(this.props.location.search);
        const theme = query.theme || DEFAULT_THEME;
        const {
          user,
          isFetchingUser,
          unsubscribing,
          unsubError,
          unsubSuccess,
          unsubList,
          updateSuccess,
          submitError,
          userIdError,
          submitEmailError,
          submitEmailSuccess
        } = this.state;
        const showForm = user && user.id && !isFetchingUser && !unsubList;
        return (
            <Layout ui={query.ui || UI_AUDI_MIN} theme={theme} embedded={query.embedded} wide={query.wide}>
                <Helmet>
                    <title>Email Preference Centre | Audi Australia</title>
                </Helmet>

                <main className="audi-forms__page">
                    <h1 className="aui-headline-3 mb-4 pt-3 pt-medium-5">Email Preference Centre</h1>
                    
                    {isFetchingUser && !unsubList && <div className="my-5">
                      <Loader><p><b>One moment, we are retrieving your details...</b></p></Loader>
                    </div>}
                    
                    {!isFetchingUser && !USE_EMAIL_FALLBACK && (user === null || !user.id || userIdError) &&
                      <div className="aui-color-text-red my-5 row">
                        <div className="col-auto"><IconCaution large /></div>
                        <div className="col">
                          <p> Sorry, there was a problem retrieving your details. Please try again later.</p>
                          <p>If you wish to unsubscribe from all communication, please contact us via <a href="tel:1300557405" style={{textDecoration:"underline"}}>1300 557 405</a> or via the online form here: <Link to={PATH_CONTACTUS} style={{textDecoration:"underline"}}>forms.audi.com.au/contact-us</Link></p>
                        </div>
                      </div>
                    }
                    
                    {unsubList && <div className="my-5">
                      {unsubscribing && <Loader><p><b>One moment, you are being unsubscribed...</b></p></Loader>}
                      {!unsubscribing && unsubError &&
                        <div className="aui-color-text-red">
                          <IconCaution large /> Sorry, there was a problem unsubscribing you.
                        </div>
                      }
                      {!unsubscribing && unsubSuccess && <>
                        <div className="aui-color-text-green">
                          <IconSelect large /> You have been unsubscribed from {unsubList.map((item,i) => {
                            let name = unsubListNames[item] || item;
                            let joiner = ", ";
                            if (i === unsubList.length - 1) { joiner = ""; }
                            if (i === unsubList.length - 2) { joiner = " and "; }
                            return (name + joiner); 
                        })}.
                        </div>
                        <p className="mb-3">Click below to manage your Audi contact preferences</p>
                        <p className="mb-5"><Button type="button" variant="primary" className="red" onClick={()=>{ this.setState({unsubList: null}); }}>Manage Preferences</Button></p>
                      </>}
                    </div>}
                    
                    {showForm && <div>
                      <p className="mb-5">
                        Personalise your Audi email experience by choosing your preferences. You can update these details at any time.
                      </p>

                      <Formik onSubmit={this.handleSubmit} initialValues={user}>
                        {({values, setFieldValue, setValues, isSubmitting}) => (
                            <Form className="audi-form">
                                
                                {isSubmitting && <div className="spinner-wrapper"><Spinner isContinuous /></div>}
                                {submitError &&
                                  <div className="aui-color-text-red my-5">
                                    <IconCaution large /> Sorry, there was a problem updating your preferences. Please try refreshing the page, or try again later.
                                  </div>
                                }
                                {updateSuccess && !isSubmitting &&
                                  <div className="aui-color-text-green my-5">
                                    <IconSelect large /> Your preferences have been updated.
                                  </div>
                                }
                                
                                <div className="row">
                                  <div className="col-12 col-medium-6">
                                    <h5 className="aui-headline-4 mb-3">Audi Vehicles</h5>
                                    <PreferenceOption name="pcProductOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcEventsOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcNewsOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <h5 className="aui-headline-4 mb-3">Audi Lifestyle</h5>
                                    <PreferenceOption name="pcF1OptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcMagazineOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcExperienceOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcFoundationOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                  </div>
                                  <div className="col-12 col-medium-6">
                                    <h5 className="aui-headline-4 mb-3">Your Audi</h5>
                                    <PreferenceOption name="pcStoreOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcOffersOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <PreferenceOption name="pcFinancialServiceOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <h5 className="aui-headline-4 mb-3">Audi Feedback</h5>
                                    <PreferenceOption name="pcSurveyOptIn" setFieldValue={setFieldValue} values={values} theme={theme} />
                                    <hr className="my-4" />
                                    <PreferenceOption name="pcsmsOptIn" setFieldValue={setFieldValue} values={values} className="small" theme={theme} />
                                    <div className="form-field small">
                                      <Checkbox name="all"
                                        label={<i>Check/uncheck all</i>}
                                        checked={values.pcProductOptIn && values.pcNewsOptIn && values.pcOffersOptIn && values.pcF1OptIn && values.pcMagazineOptIn && values.pcExperienceOptIn && values.pcFoundationOptIn && values.pcEventsOptIn && values.pcSurveyOptIn && values.pcStoreOptIn}
                                        value=""
                                        onChange={() => { this.handleToggleAll(values, setValues); }}
                                        theme={theme === "dark" ? "light" : null}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="form-field submit">
                                    <Button type="submit" variant="primary" className="red">Save Preferences</Button>
                                </div>
                            </Form>
                        )}
                      </Formik>
                    </div>}
                    
                    {userIdError && USE_EMAIL_FALLBACK && !submitEmailSuccess && <div>
                      <p className="mb-3">
                          <IconCaution large /> Sorry, there was a problem retrieving your details. 
                          <br />If you would like to unsubscribe from all Audi emails, please enter your email address below.
                          <br />If you have a myAudi Australia account, you can also manage your email preferences at any time from <a href="https://myaudi.com.au/profile" target="_blank" rel="nofollow noreferrer" className="aui-textlink">your profile page</a>.
                      </p>
                      <Formik validate={this.validateEmailForm} onSubmit={this.handleSubmitEmail} initialValues={{emailAddress: ""}}>
                        {(formikProps) => (
                            <Form className="audi-form" style={{maxWidth: "500px"}}>
                                {formikProps.isSubmitting && <div className="spinner-wrapper"><Spinner isContinuous /></div>}
                                <div className="form-field">
                                    <TextField
                                        name="emailAddress"
                                        label="Email address"
                                        value={formikProps.values.emailAddress}
                                        onChange={(v) => { formikProps.setFieldValue("emailAddress", v); }}
                                        isInvalid={formikProps.errors.emailAddress && formikProps.touched.emailAddress}
                                        invalidMessage={formikProps.errors.emailAddress}
                                        onBlur={() => { formikProps.setFieldTouched("emailAddress"); }}
                                        theme={theme === "dark" ? "light" : null}
                                    />
                                </div>
                                {submitEmailError && <>
                                    <p className="aui-color-text-red mb-3">
                                        We were unable to find that email address in our records. Please check the spelling and try again.
                                    </p>
                                    <p className="mb-5">
                                        For further assistance, please email <a href="mailto:customerassistance@audi-info.com.au" className="aui-textlink">customerassistance@audi-info.com.au</a> or call us on <a href="tel:1800502834" className="aui-textlink">1800 502 834</a> between 8am and 6pm AEST, Monday to Friday.
                                    </p>
                                </>}
                                <div className="form-field recaptcha">
                                    <ReCAPTCHA sitekey={process.env.RAZZLE_GOOGLE_RECAPTCHA_KEY} onChange={(v) => { this.setState({capVal: v}); }} />
                                </div>
                                <div className="form-field submit">
                                    <Button type="submit" variant="primary" className="red" disabled={!this.state.capVal}>Unsubscribe</Button>
                                </div>
                            </Form>
                        )}
                      </Formik>
                    </div>}
                    
                    {USE_EMAIL_FALLBACK && submitEmailSuccess && <div className="aui-color-text-green mb-5">
                        <IconSelect large /> You have unsubscribed.
                    </div>}
                </main>
            </Layout>
        );
    }

}

export default PreferenceCentre;
